import { endpointPath } from "src/hooks/useFetch";
import {
  AIMessage,
  AIToolCall,
  AIToolResult,
  BackendChatEnvelope,
  ChatRequest,
  ChatRequestMessage,
  ChatResponse,
  ContentBlock,
  TelemetryContext,
  ToolHandler,
} from "./types";
import { TELEMETRY_TOOLS } from "./tools";
import { buildSystemPrompt } from "./systemPrompt";
import { useSkillsStore } from "./skills/skillsStore";
import { useMemoryStore } from "./memories/memoryStore";

/**
 * Wire format contract with the telemetry-backend Bedrock proxy:
 *
 *   POST /api/ai/chat
 *     Headers: Authorization: Bearer <auth0 token>
 *     Body:    ChatRequest
 *     200:     BackendChatEnvelope
 *
 * The backend is responsible for:
 *   - Validating the Auth0 token AND checking the executive claim
 *   - Calling Bedrock InvokeModel with the request body unchanged
 *   - Returning the raw Claude response inside { success: true, response }
 *   - On error, returning { success: false, error: "..." } with HTTP 200 or 4xx
 *
 * The renderer drives the tool-use loop: when Claude responds with stop_reason
 * 'tool_use', we execute each tool locally, append a user-role message
 * containing tool_result blocks, and call /api/ai/chat again. This is the same
 * pattern the CytoCanvas KateAI uses, just over HTTPS instead of IPC.
 */

// Bumped from 12 → 30 to reduce the "I need to re-pull data, my prior results
// aren't in memory" failure when the user asks Kate to continue a multi-turn
// analysis. Note: only the final assistant text from each prior turn rides in
// history (intermediate tool_use/tool_result blocks are NOT persisted across
// turns). A future improvement is to keep the last few tool_result payloads
// alongside the text — but bumping this helps even without that.
const MAX_HISTORY_MESSAGES = 30;
// Generous to accommodate skill-driven analyses (e.g. weekly-fleet-review
// runs ~8 sequential SQL queries before summarizing). If we hit this cap we
// don't exit silently — see the fallback below.
const MAX_TOOL_LOOPS = 30;
// Per-call output cap. 4096 was much too tight for Opus 4.7 — the model emits
// long preamble text alongside its tool_use blocks during analytical loops,
// and a single oversized turn would return stop_reason="max_tokens" which the
// loop treated identically to "end_turn", silently truncating Kate mid-thought
// (see the explicit handling below). 16384 leaves comfortable headroom while
// still well under Bedrock's per-call ceiling for this model.
const MAX_OUTPUT_TOKENS = 16_384;
const STRIPPED_IMAGE_TAG = "[image: omitted from history]";

const stripBase64Images = (text: string): string =>
  text.replace(
    /!\[([^\]]*)\]\(data:image\/[^)]*\)/g,
    (_, alt) => `[image: ${alt || "omitted"}]`,
  );

const cleanMessageForHistory = (m: AIMessage): ChatRequestMessage => ({
  role: m.role,
  content: stripBase64Images(m.content),
});

interface SendMessageOpts {
  userMessage: string;
  conversationHistory: AIMessage[];
  context: TelemetryContext;
  toolHandler: ToolHandler;
  getAccessTokenSilently: () => Promise<string>;
  onToolStart?: (toolName: string, input: Record<string, unknown>) => void;
}

const callBackend = async (
  request: ChatRequest,
  getAccessTokenSilently: () => Promise<string>,
): Promise<ChatResponse> => {
  const token = await getAccessTokenSilently();
  const url = endpointPath("/ai/chat");
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`AI backend ${res.status}: ${text || res.statusText}`);
  }
  const envelope = (await res.json()) as BackendChatEnvelope;
  if (!envelope.success || !envelope.response) {
    throw new Error(envelope.error || "AI backend returned an unsuccessful envelope");
  }
  return envelope.response;
};

const extractTextFromContent = (blocks: ContentBlock[]): string => {
  const out: string[] = [];
  for (const b of blocks) {
    if (b.type === "text") out.push(b.text);
  }
  return out.join("\n\n").trim();
};

const extractToolUses = (blocks: ContentBlock[]): AIToolCall[] => {
  const out: AIToolCall[] = [];
  for (const b of blocks) {
    if (b.type === "tool_use") {
      out.push({ id: b.id, name: b.name, input: b.input });
    }
  }
  return out;
};

const toolResultToContent = (
  result: unknown,
  isError?: boolean,
): { type: "tool_result"; tool_use_id: string; content: string; is_error?: boolean } => {
  let serialized: string;
  try {
    serialized = JSON.stringify(result);
  } catch {
    serialized = String(result);
  }
  if (serialized.length > 32_000) {
    serialized = serialized.slice(0, 32_000) + "\n\n[truncated]";
  }
  return {
    type: "tool_result",
    tool_use_id: "", // filled in by caller
    content: serialized,
    is_error: isError,
  };
};

export async function sendChatMessage(opts: SendMessageOpts): Promise<AIMessage> {
  const {
    userMessage,
    conversationHistory,
    context,
    toolHandler,
    getAccessTokenSilently,
    onToolStart,
  } = opts;

  const history = conversationHistory
    .slice(-MAX_HISTORY_MESSAGES)
    .map(cleanMessageForHistory);

  const messages: ChatRequestMessage[] = [
    ...history,
    { role: "user", content: userMessage },
  ];

  const allToolCalls: AIToolCall[] = [];
  const allToolResults: AIToolResult[] = [];
  const accumulatedText: string[] = [];

  let lastStopReason: ChatResponse["stop_reason"] | null = null;

  for (let loop = 0; loop < MAX_TOOL_LOOPS; loop++) {
    // Re-read skills + memories on every loop so a successful add_memory or
    // delete_memory in this turn is visible on the very next sub-call.
    const skills = useSkillsStore.getState().skills;
    const memories = useMemoryStore.getState().memories;

    const response = await callBackend(
      {
        messages,
        system: buildSystemPrompt(context, { skills, memories }),
        tools: TELEMETRY_TOOLS,
        max_tokens: MAX_OUTPUT_TOKENS,
        temperature: 0.4,
      },
      getAccessTokenSilently,
    );

    lastStopReason = response.stop_reason;

    const text = extractTextFromContent(response.content);
    if (text) accumulatedText.push(text);

    if (response.stop_reason !== "tool_use") {
      break;
    }

    const toolCalls = extractToolUses(response.content);
    if (toolCalls.length === 0) break;

    // Append the assistant message with the original content blocks so the
    // next turn's tool_result blocks can reference the correct tool_use ids.
    messages.push({ role: "assistant", content: response.content });

    const resultBlocks: ContentBlock[] = [];
    for (const call of toolCalls) {
      allToolCalls.push(call);
      onToolStart?.(call.name, call.input);
      let result: unknown;
      let isError = false;
      try {
        result = await toolHandler(call.name, call.input);
      } catch (err) {
        result = { error: (err as Error).message };
        isError = true;
      }
      allToolResults.push({ toolCallId: call.id, result, isError });
      const block = toolResultToContent(result, isError);
      block.tool_use_id = call.id;
      resultBlocks.push(block);
    }

    messages.push({ role: "user", content: resultBlocks });
  }

  // Fallback: we exhausted MAX_TOOL_LOOPS while the model still wanted to keep
  // calling tools. Without this, the user sees only whatever intro text Kate
  // wrote on her first turn — the actual analysis goes unsummarized. Force a
  // single text-only response so she can wrap up with what she has.
  if (lastStopReason === "tool_use") {
    onToolStart?.("__force_summary__", {});
    try {
      const skills = useSkillsStore.getState().skills;
      const memories = useMemoryStore.getState().memories;
      const finalResponse = await callBackend(
        {
          messages,
          system:
            buildSystemPrompt(context, { skills, memories }) +
            "\n\n## IMPORTANT\nYou have used many tool calls and must now stop calling tools. Summarize what you have found so far in plain text. If your analysis is incomplete, say so explicitly and offer to continue when the user asks.",
          tools: TELEMETRY_TOOLS,
          tool_choice: { type: "none" },
          max_tokens: MAX_OUTPUT_TOKENS,
          temperature: 0.4,
        },
        getAccessTokenSilently,
      );
      const finalText = extractTextFromContent(finalResponse.content);
      if (finalText) accumulatedText.push(finalText);
    } catch (err) {
      accumulatedText.push(
        `_(I hit my tool-call budget and couldn't summarize cleanly: ${(err as Error).message})_`,
      );
    }
  }

  // Surface output-token truncation explicitly. Previously this was treated
  // identically to "end_turn", so the user saw a half-finished narration with
  // no idea why Kate stopped. The most common trigger is a long preamble +
  // tool_use block in the same turn that pushes past max_tokens; the loop
  // then exits because stop_reason !== "tool_use". Telling the user what
  // happened lets them prompt "please continue" with intent.
  if (lastStopReason === "max_tokens") {
    accumulatedText.push(
      "_(Response was cut off because I hit my per-turn output budget. " +
        'Ask me to "continue" and I\'ll pick up where I left off.)_',
    );
  }

  return {
    id:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (crypto as unknown as { randomUUID: () => string }).randomUUID()
        : `msg_${Date.now()}`,
    role: "assistant",
    content: accumulatedText.join("\n\n").trim() || "(no response)",
    timestamp: new Date(),
    toolCalls: allToolCalls.length > 0 ? allToolCalls : undefined,
    toolResults: allToolResults.length > 0 ? allToolResults : undefined,
  };
}
